/**
 * Reynisfjara's PUFİN — the Atlantic puffin — in the shared 120×120 LINE asset box.
 *
 * The Home tab, all four passcode dots and the chat wallpaper draw this bird.
 *
 * WHY THIS REPLACED THE SEA STACKS
 *
 * The beach drew its own rocks: three sea stacks standing in the water, with a
 * hexagonal basalt column on the wallpaper. They were redrawn once already after
 * the owner called them slapdash, and the redraw fixed the drawing without fixing
 * the problem — which is the SUBJECT. A sea stack is a dark lump. There is no
 * angle at which a dark lump is charming, it shares a silhouette family with every
 * other mass in this set, and on a chat theme aimed at LINE's own market it asks
 * the reader to find a rock interesting.
 *
 * The puffin is the same beach's other famous inhabitant and it is everything the
 * rock was not: it is an ANIMAL, it is round, it has a face, and its bill is one
 * of the few silhouettes in nature that cannot be mistaken for anything else. It
 * is also the single most kawaii thing available anywhere in these twenty-two
 * themes, which on a Japanese store is not a small consideration.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - THE BILL. Deep, triangular and nearly a third of the bird's length. It is the
 *    whole identification: a chunky black-and-white seabird with a small bill is a
 *    guillemot, and with no bill at all it is a penguin. Everything else here can
 *    be simplified; this cannot.
 *  - THE PEAR. Narrow at the head, wide at the belly. Drawn as one even oval the
 *    bird becomes a skittle.
 *  - THE PALE FACE. A puffin's face is a white disc set into a black head, and it
 *    is what the eye sits in. Without it the eye is a hole in a silhouette.
 *  - THE FEET SHOW. Two of them, orange, poking out below the belly. A puffin
 *    standing on nothing is a puffin swimming.
 *
 * Every subpath is wound clockwise so a nonzero fill unions them
 * ([[line-shared-glyph-modules]]).
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/* ---------------------------------------------------------------- geometry -- */

/**
 * The BODY: head and belly as one continuous pear, facing right.
 *
 * A puffin has no neck worth drawing at this size, so this is a single outline
 * rather than a head sitting on a body — the notch a neck would put in the
 * silhouette is the first thing that closes up at 22px, and a bird with a half
 * closed notch reads as a bird with a lump.
 *
 * The head is 42 units across and the belly 53. That difference is small on paper
 * and it is the entire difference between a puffin and a bowling pin.
 */
export const PUFFIN_BODY =
  'M55,8'
  // The crown does NOT overhang the bill. It used to reach its widest point at
  // exactly the height the bill leaves the head, so there was a shelf of forehead
  // standing out past the bill's root — and a shelf above a horizontal wedge is
  // the last thing that was still saying flipper. On a real puffin the culmen
  // runs straight into the forehead with no step at all.
  + ' C65,8 70,16 69,26'          // crown down the right to the cheek
  + ' C69,35 68,44 66,50'         // the face, where the bill attaches
  + ' C77,58 83,74 78,87'         // the breast, bulging out
  + ' C72,96 60,100 51,100'       // the belly's underside
  + ' C38,100 26,92 24,79'        // round the bottom-left
  + ' C22,68 25,58 31,50'         // up the flank
  // THE NECK. Six units of step where the back meets the nape, and it is the
  // second thing this glyph needed rather than the first.
  //
  // Drawn without it the head and the body were one smooth ovoid, so the eye had
  // no way to find where the head ended — and a big triangle coming off the side
  // of an ovoid is not a bill, it is a FLIPPER. Two passes were spent sharpening
  // the bill before the actual fault turned out to be six units of outline
  // thirty degrees away from it.
  + ' C34,46 36,43 37,39'
  + ' C38,24 44,8 55,8 Z';        // the nape back to the crown

/* ------------------------------------------------------------------- bill --
 * The bill is built rather than written so its outline, its coloured tip and the
 * groove between them share EXACTLY the same edges. Written by hand as three
 * paths they drift by a unit and the bird grows a bright seam down its face.
 */

/** Where the bill starts and ends, and how deep it is at each end. */
// RAISED and SHARPENED from the first pass (root 33..58, tip 44..47).
//
// Two faults, and both only showed in the UNLIT silhouette, where the bill and the
// body are one flat shape. The bill's underside sat at 58, which is exactly where
// the breast starts to bulge, so the notch between them was about two units deep —
// and a big triangle stuck on a round body with no notch under it is a FLIPPER.
// The bird read as a seal at 30px. The tip was also three units deep, which is
// blunt enough to be a flipper's end as well.
//
// The bill now sits four units higher and ends in one and a half units, and the
// face edge above was pulled in to 68 to open the notch further. A puffin's bill
// meets its breast at a clear step; that step is the whole read.
//
// It is also much SHORTER. At 39 long against 25 deep it ran at a ratio of 1.56,
// and a shallow horizontal wedge is a flipper whatever is behind it. A real
// Atlantic puffin's bill is very nearly as deep as it is long; at 32 by 26 this
// one runs at 1.23, which is inside the range no other bird on that beach has.
const BILL_X0 = 64, BILL_X1 = 96;
const BILL_TOP0 = 25.5, BILL_TOP1 = 43.5;
const BILL_BOT0 = 55, BILL_BOT1 = 46.5;

/** The top edge (the culmen) arches UP; the bottom edge (the gonys) bulges DOWN.
 *  Two straight edges make a wedge, and a wedge is a beak on a cartoon crow. */
const billTop = ( t: number ): [ number, number ] => [
  BILL_X0 + ( BILL_X1 - BILL_X0 ) * t,
  BILL_TOP0 + ( BILL_TOP1 - BILL_TOP0 ) * t - Math.sin( t * Math.PI ) * 3.2,
];
const billBot = ( t: number ): [ number, number ] => [
  BILL_X0 + ( BILL_X1 - BILL_X0 ) * t,
  BILL_BOT0 + ( BILL_BOT1 - BILL_BOT0 ) * t + Math.sin( t * Math.PI ) * 2.4,
];

/** A slice of the bill between two points along its length, as one closed path. */
function billSlice( a: number, b: number ): string {
  const N = 14;
  const pts: [ number, number ][] = [];
  for ( let i = 0; i <= N; i++ ) { pts.push( billTop( a + ( b - a ) * ( i / N ) ) ); }
  for ( let i = N; i >= 0; i-- ) { pts.push( billBot( a + ( b - a ) * ( i / N ) ) ); }
  return cw( pts );
}

/** The whole bill. It overlaps the head by six units, so the two union into one
 *  silhouette with no seam at the root. */
export const PUFFIN_BILL = billSlice( 0, 1 );

/**
 * The bill's OUTER THIRD, which on a real puffin is the red-orange plate.
 *
 * The band starts at 0.44 rather than halfway: the plate is the outer part of a
 * bill that tapers, so an even split puts more colour on the bird than it has.
 */
export const PUFFIN_BILL_TIP = billSlice( 0.44, 1 );

/** The pale GROOVE between the two plates — one unit of the bird's own face
 *  colour, and the detail that says puffin rather than parrot. */
export const PUFFIN_BILL_GROOVE = billSlice( 0.41, 0.455 );

/**
 * The FACE: the pale disc set into the head.
 *
 * Its right edge is slightly CONCAVE, so it tucks in behind the bill root rather
 * than butting against it — a straight edge there leaves a pale line standing
 * proud of the bill at every size above about 40px.
 */
export const PUFFIN_FACE =
  'M67,27'
  + ' C61,15 47,13 39,22'
  + ' C31,30 31,44 39,52'
  + ' C45,58 55,58 62,55'
  // the bottom-right rounds back UP to the bill root rather than running out to a
  // corner. Squared off there — the first pass — the face had a straight vertical
  // edge under the bill, and a straight edge on an otherwise round bird is the one
  // thing the eye finds first.
  + ' C65,53 64,39 67,27 Z';

/** The EYE. Round and large, because this is the one place in the whole run where
 *  a friendly face is the actual brief. */
export const PUFFIN_EYE =
  'M49,29 a6,6 0 1,0 12,0 a6,6 0 1,0 -12,0 Z';

/** The dark line running back from the eye — a real puffin has one, and it is
 *  what stops the eye reading as a spot stuck on a blank face. */
export const PUFFIN_EYE_LINE =
  'M49,35 C43,39 37,41 32,40 C37,44 44,43 49,40 Z';

/**
 * The WING: a folded teardrop lying along the flank.
 *
 * Drawn a shade off the body rather than in a different colour. A puffin's wing is
 * the same black as its back; what separates them on a real bird is the edge, and
 * a flat fill has to say that with tone.
 */
export const PUFFIN_WING =
  // NARROWER and set further BACK than the first pass, which ran a 27-unit oval
  // down the middle of the belly and read as a shadow somebody had left on the
  // bird. A folded wing lies along the flank, so it belongs against the back edge
  // with most of the breast clear of it.
  'M38,54'
  + ' C48,54 56,68 54,82'
  + ' C52,92 43,95 38,89'
  + ' C32,82 31,61 38,54 Z';

/** The two FEET, webbed, poking out under the belly. The near one is bigger and
 *  further forward; drawn as a matched pair they read as one wide orange base. */
export const PUFFIN_FEET = [
  // both start at 92 rather than 95, so they overlap the belly properly instead of
  // meeting it on a line — two shapes that merely touch leave a hairline seam the
  // renderer antialiases into a visible speck
  'M54,92 C62,93 70,101 71,105.5 C71,107 69,107.5 67,107.5'
  + ' L52,107.5 C49,107.5 49,97 54,92 Z',
  'M40,92 C46,93 52,101 53,105.5 C53,107 51,107.5 49,107.5'
  + ' L38,107.5 C35,107.5 35,97 40,92 Z',
].join( ' ' );

/** The whole bird as ONE path — feet, body and bill. This is what the idle tab and
 *  the unentered passcode dot draw. */
export const PUFFIN = `${ PUFFIN_FEET } ${ PUFFIN_BODY } ${ PUFFIN_BILL }`;

/**
 * A SIMPLER bird for the wallpaper's smaller grains: body and bill, no feet.
 *
 * Same reason Tallinn's simple flake exists. At a third of this box the feet are
 * two pixels of orange under the belly and read as a smudge the bird is standing
 * in; the bill is what carries the shape and it carries it all the way down.
 */
export const PUFFIN_SIMPLE = `${ PUFFIN_BODY } ${ PUFFIN_BILL }`;

/**
 * A SAND EEL — the wallpaper's second element.
 *
 * Puffins carry them home crosswise in the bill, a dozen at a time, and it is the
 * picture everybody has of this bird. It is the same relationship Madrid's loose
 * churro has with its cup: the second element is one item off the thing the grain
 * is doing, rather than an unrelated mark that has to justify itself.
 */
export const PUFFIN_FISH = ( () => {
  const body = 'M44,60 C52,52 70,52 79,60 C70,68 52,68 44,60 Z';
  const tail = 'M45,60 L33,52 C36,57 36,63 33,68 Z';
  return `${ body } ${ tail }`;
} )();

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered puffins, on the NEAR-BLACK upper zone this skin's passcode
 * screen paints (#10141A).
 *
 * These are the BODY colours, and they are the one place this glyph has to argue
 * with the bird. A real puffin's back is black, and black on a near-black zone is
 * nothing — so the four walk UP out of the dark instead: wet slate, then the grey
 * of a bird with the sky behind it, then dry sand, then the pale driftwood at the
 * top of this beach.
 *
 * Measured on that zone: 3.28 / 4.72 / 6.11 / 8.04 to 1.
 */
export const PUFFIN_PC_COLORS = [ '#5D6773', '#7D848D', '#9A907F', '#C5A98F' ];

/**
 * ONE tone for every unentered puffin — the frame never changes per digit (the
 * rule Aurora's orbs settled).
 *
 * 1.55:1 on the near-black. Present, and plainly a bird nobody has woken yet.
 */
export const PUFFIN_PC_OFF = '#2E353E';

/** The BILL's two plates and the FEET, the same on every digit.
 *
 *  Both come out of the theme's own tokens rather than off a photograph: `warning`
 *  (#BA9D5F) and `errorFocused` (#C2594D). A real puffin's bill is a hot
 *  orange-red, and dropped into this palette at that saturation it was the only
 *  loud thing in a theme whose three tags are Nordic, Muted and Cinematic. These
 *  two are the warmest colours the theme already owns, and warm against this much
 *  cold slate is plenty. */
export const PUFFIN_BILL_OUTER = '#C2594D';
export const PUFFIN_BILL_INNER = '#BA9D5F';
export const PUFFIN_FOOT = '#C2594D';

/**
 * A per-digit lean, alternating side, about the FEET.
 *
 * Generous by this run's standards, and it should be: a standing bird shifts its
 * weight, and a row of four at slightly different angles is a row of four birds
 * rather than one bird printed four times. A leaning rock is a landslide; a
 * leaning puffin is just a puffin.
 */
export const PUFFIN_PC_TILTS = [ -8, 6, -4, 10 ];
